import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { asyncConnect } from 'react-async-client';
import { Field } from 'react-final-form';
import { Button, Form } from 'antd';
import Translate from 'react-translate-component';
import { path, pathOr } from 'ramda';
import moment from 'moment';
import createDecorator from 'final-form-calculate';

import { currency } from 'constants/dictionaries';
import { updateEvent, getOrganizations  } from 'actions/asyncActions';
import DatePicker from 'components/common/form/DatePicker';
import InputHidden from 'components/common/form/InputHidden';
import FieldWithLabel from 'components/common/form/FieldWithLabel';
import withFormWrapper from 'components/hocs/withFormWrapper';
import Input from 'components/common/form/Input';
import { SelectLoadComponent } from 'components/common/form/Select';
import DepartmentField from 'components/views/addRequest/fields/DepartmentField';
import Select from 'components/common/form/Select';
import { formItemLayout } from 'constants/formItemLayout';
import { SALARY_MASK } from 'constants/regexp';
import InputNumberMask from 'components/common/form/InputNumberMask';
import Text from 'components/common/form/Text';
import RadioButtons from 'components/common/form/RadioButtons';
import { WORKING_WAITING_DICT, WORKING_PASSED_CODE } from 'constants/events';
import { required } from 'utils/validators';
import { DATE_FORMATS } from 'constants/dateFormats';
import { DEFAULT_PROBATION } from 'constants/events';
import { formatMoney } from 'helpers/fieldsHelpers';
import ListenerField from 'components/common/form/ListenerField';

const moreThanZero = value => parseInt(value, 10) > 0 ? undefined : <Translate content='errors.required' />;

const getProbation = pathOr(DEFAULT_PROBATION, ['projectData', 'probation']);
const getPositionName = path(['projectData', 'positionName']);
const getOrganizationId = path(['projectData', 'organizationId']);
const getDepartmentId = path(['projectData', 'departmentId']);
const getOrganizationName = path(['projectData', 'organizationName']);
const isOrgAndDepRequired = path(['projectData', 'needOrgDepartVocabs']);

const decoratorsFromProps = ({ event }) => createDecorator({
    field: 'details.eventResult',
    updates: value => ({
        details: {
            eventResult: value,
            comeToWorkDate: value === WORKING_PASSED_CODE ? moment().format(DATE_FORMATS.format) : undefined,
            endDate:  value === WORKING_PASSED_CODE ? moment().add(getProbation(event), 'days').format(DATE_FORMATS.format) : undefined,
            workToDate: undefined,
            positionName: undefined,
            salary: undefined,
            valSalary: undefined,
            gonorar: undefined,
            valGonorar: undefined,
            personState: undefined,
            resultComment: undefined
        }
    })
});

@asyncConnect({
    formAction: updateEvent
        .withSuccessHandler(({ closeModal }) => closeModal())
        .withOptions({ autoReset: true }),
    getOrganizations: getOrganizations.withOptions({ dispatchOnMount: true, autoReset: true })
})
@withFormWrapper({
    mapPropsToValues: ({ event }) => ({
        id: event.id,
        details: {
            positionName: getPositionName(event),
            organizationId: event.projectData.organizationId,
        }
    }),
    decoratorsFromProps,
})
export default class EventWaitingForWorkForm extends Component {
    static propTypes = {
        formAction: PropTypes.object.isRequired,
        event: PropTypes.object.isRequired,
        close: PropTypes.func.isRequired,
        closeModal: PropTypes.func.isRequired,
        form: PropTypes.object,
        handleSubmit: PropTypes.func,
        getOrganizations: PropTypes.object.isRequired,
        change: PropTypes.func.isRequired
    }

    renderComeout = () => {
        const { event, getOrganizations, getOrganizations: { data: organizations } } = this.props;
        const disableOrgFields = !isOrgAndDepRequired(event);

        return (
            <div>
                <Field
                    name='details.comeToWorkDate'
                    component={DatePicker}
                    validate={required}
                    label={{ content: 'events.comeToWorkDate', required: true }}
                />
                <Field
                    name='details.endDate'
                    component={DatePicker}
                    allowClear={false}
                    validate={required}
                    label={{ content: 'events.endOfProbation', required: true }}
                />
                <Translate content='events.workPlace' component='div' className='modal-block-header m-b-15' />
                {disableOrgFields ? (
                    <Fragment>
                        <Field
                            name='details.organizationId'
                            component={InputHidden}
                            hidden={true}
                        />
                        <FieldWithLabel label='request.company'>
                            {getOrganizationName(event)}
                        </FieldWithLabel>
                    </Fragment>
                ) : (
                    <Field
                        name='details.organizationId'
                        component={Select}
                        options={organizations}
                        validate={required}
                        dictionaryAction={getOrganizations}
                        label={{ content: 'request.company', required: true }}
                        placeholder={<Translate content='placeholders.chooseCompany' />}
                    />
                )}
                <Field
                    name='details.workToDate'
                    component={DatePicker}
                    label={{ content: 'events.workToDate' }}
                />
                <Field
                    name='details.positionName'
                    component={Input}
                    label={{ content: 'events.positionName', required: true }}
                    validate={required}
                />
                <ListenerField
                    listenFieldName={'details.organizationId'}
                >
                    {(values) => (
                            <DepartmentField
                                name='details.departmentId'
                                organization={path(['details', 'organizationId'], values) || 0}
                            />
                        )
                    }
                </ListenerField>
                <Form.Item label={<Translate content='events.salary' />} {...formItemLayout} required={true}>
                    <Field
                        name='details.salary'
                        component={InputNumberMask}
                        notWrap={true}
                        validate={moreThanZero}
                        containerClass='inline'
                        step='1000'
                        mask={SALARY_MASK}
                        notError={true}
                        formatter={formatMoney}
                    />
                    <Field
                        name='details.valSalary'
                        component={SelectLoadComponent}
                        loadName={currency}
                        namePath='code'
                        placeholder={<Translate content='placeholders.currency' />}
                        notWrap={true}
                        containerClass='inline'
                        style={{ width: '100px' }}
                        allowClear
                        validate={required}
                    />
                </Form.Item>
                <Translate content='events.feeOfAgency' component='div' className='modal-block-header m-b-15' />
                <Form.Item label={<Translate content='events.gonorar' />} {...formItemLayout}>
                    <Field
                        name='details.gonorar'
                        component={InputNumberMask}
                        notWrap={true}
                        containerClass='inline'
                        step='1000'
                        mask={SALARY_MASK}
                        formatter={formatMoney}
                    />
                    <Field
                        name='details.valGonorar'
                        component={SelectLoadComponent}
                        loadName={currency}
                        namePath='code'
                        placeholder={<Translate content='placeholders.currency' />}
                        notWrap={true}
                        containerClass='inline'
                        style={{ width: '100px' }}
                        allowClear
                    />
                </Form.Item>
                <Translate content='events.additionaly' component='div' className='modal-block-header m-b-15' />
                <Field
                    name='details.resultComment'
                    component={Text}
                    label={{ content: 'events.comment' }}
                />
            </div>
        )
    }

    renderDidntComeout = () => {
        const { excludeReasons } = this.props.event;

        return (
            <div>
                <Field
                    name='details.personState'
                    component={Select}
                    namePath='text'
                    options={excludeReasons}
                    validate={required}
                    label={{ content: 'events.reason', required: true }}
                    allowClear
                />
                <Field
                    name='details.resultComment'
                    component={Text}
                    validate={required}
                    label={{ content: 'events.comment', required: true }}
                />
            </div>
        )
    }

    renderFields = (values) => {
        const eventResult = path(['details', 'eventResult'], values);

        if (!eventResult) {
            return null;
        }

        return eventResult === WORKING_PASSED_CODE ? this.renderComeout() : this.renderDidntComeout();
    }

    onChangeResult = result => {
        const { event, form: { batch, change } } = this.props;
        const isStartWorking = result === WORKING_PASSED_CODE;

        const positionName = isStartWorking ? getPositionName(event) : undefined;
        const orgId = isStartWorking ? getOrganizationId(event) : undefined;
        const depId = isStartWorking ? getDepartmentId(event) : undefined;

        batch(() => {
            change('details.organizationId', orgId);
            change('details.departmentId', depId);
            change('details.positionName', positionName);
        });
    }

    render() {
        const { handleSubmit, close }  = this.props;

        return (
            <div className='panel-view waiting-form'>
                <Field
                    name='details.eventResult'
                    component={RadioButtons}
                    radioTranslate='events'
                    validate={required}
                    onChange={this.onChangeResult}
                    options={WORKING_WAITING_DICT}
                    label={{ content: 'events.result', required: true }}
                />

                <ListenerField
                    listenFieldName={'details.eventResult'}
                >
                    {this.renderFields}
                </ListenerField>
                <Button className='card-button close-button' onClick={close}>
                    <Translate content='events.cancel' />
                </Button>
                <Button onClick={handleSubmit} type='primary'>
                    <Translate content='events.saveEventResult' />
                </Button>
            </div>
        );
    }
}
